import { signal, effect } from '@preact/signals';
import { dailyPick } from './daily-chapter.js';
import { secureStorage } from '../storage/secureStorage.js';

/**
 * Local-only verse-of-the-day notifications. Schedules a setTimeout
 * for the user's chosen hour; on fire it asks the SW to show a
 * notification with today's daily-pick verse. No server, no push
 * subscription, no VAPID keys.
 *
 * iOS Safari only delivers notifications when the app is installed
 * as a PWA (Add to Home Screen). The opt-in toggle surfaces this
 * inline.
 */

type State = { enabled: boolean; hour: number };
const STORAGE_KEY = '_nc1';
const SNOOZE_KEY = '_ns1';

const DEFAULTS: State = { enabled: false, hour: 7 };

function load(): State {
  if (typeof localStorage === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<State>;
    return {
      enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : false,
      hour: typeof parsed.hour === 'number' && parsed.hour >= 0 && parsed.hour < 24
        ? parsed.hour
        : 7,
    };
  } catch {
    return DEFAULTS;
  }
}

export const notificationPrefs = signal<State>(load());

let timer: ReturnType<typeof setTimeout> | null = null;

function persist(s: State): void {
  void secureStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

function nextFireMs(hour: number): number {
  const now = new Date();
  const target = new Date(now);
  target.setHours(hour, 0, 0, 0);
  if (target.getTime() <= now.getTime()) target.setDate(target.getDate() + 1);
  return target.getTime() - now.getTime();
}

async function fire(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const pick = dailyPick.peek();
  if (!pick) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    await reg.showNotification('Verse of the day', {
      body: `${pick.bookName} ${pick.chapter}`,
      icon: '/icons/icon-192.png',
      tag: 'vod',
      data: { book: pick.book, chapter: pick.chapter },
    });
  } catch {
    /* ignore */
  }
}

function reschedule(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  const { enabled, hour } = notificationPrefs.value;
  if (!enabled) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  timer = setTimeout(async () => {
    await fire();
    reschedule();
  }, nextFireMs(hour));
}

if (typeof window !== 'undefined') {
  effect(() => {
    persist(notificationPrefs.value);
    reschedule();
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) reschedule();
  });
  window.addEventListener('focus', reschedule);
}

export function setNotificationsEnabled(enabled: boolean): void {
  notificationPrefs.value = { ...notificationPrefs.value, enabled };
}

export function setNotificationHour(hour: number): void {
  if (hour < 0 || hour > 23) return;
  notificationPrefs.value = { ...notificationPrefs.value, hour };
}

export function snoozeFor30Days(): void {
  void secureStorage.setItem(SNOOZE_KEY, String(Date.now() + 30 * 86_400_000));
}

export function isSnoozed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const v = localStorage.getItem(SNOOZE_KEY);
    if (!v) return false;
    return Number(v) > Date.now();
  } catch {
    return false;
  }
}

export async function requestPermissionAndEnable(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  const result = await Notification.requestPermission();
  if (result === 'granted') {
    setNotificationsEnabled(true);
    return true;
  }
  return false;
}
